import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Container, Divider, IconButton, Stack, Tooltip, Typography, useTheme, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import invitationApi from './invitationApi';

const RSVP_ICON = {
  accepted: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  declined: <CancelOutlinedIcon fontSize="small" color="error" />,
  pending: <HourglassEmptyIcon fontSize="small" color="warning" />,
};
const RSVP_COLOR = { accepted: 'success', declined: 'error', pending: 'warning' };
const RSVP_LABEL = { accepted: 'Accepted', declined: 'Declined', pending: 'Pending' };

// One card per invitation campaign (organiser view)
function SentEventCard({ campaign, onDelete }) {
  const navigate = useNavigate();
  const { campaign_id, campaign_name, event_name, event_date, total, accepted, pending, unread_count } = campaign;
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const summary = [
    `${total || 0} invited`,
    accepted > 0 && `${accepted} attending`,
    pending > 0 && `${pending} pending`,
  ].filter(Boolean).join(' · ');

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, '&:hover': { boxShadow: 2 }, transition: 'box-shadow 120ms' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {campaign_name || event_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {campaign_name ? event_name : ''}
            </Typography>
            {event_date && (
              <Typography variant="caption" color="text.secondary" display="block">
                {new Date(event_date).toLocaleDateString()}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
              {summary}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {unread_count > 0 && (
              <Chip label={`${unread_count} msg`} size="small" color="primary" variant="filled" sx={{ fontSize: 11 }} />
            )}
            <Tooltip title="Delete invitation">
              <IconButton size="small" color="error" onClick={() => setConfirmDelete(true)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {confirmDelete ? (
          <Stack direction="row" spacing={1} mt={1.5} alignItems="center">
            <Typography variant="caption" color="error">Delete this invitation?</Typography>
            <Button size="small" color="error" variant="contained"
              sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => onDelete(campaign_id)}>
              Delete
            </Button>
            <Button size="small" sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Button
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none', fontSize: 12, mt: 1.5 }}
            onClick={() => navigate(`/invitations/create?campaign_id=${campaign_id}`)}
          >
            Manage
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// One card per received invitation
function ReceivedCard({ inv, onRsvp }) {
  const navigate = useNavigate();
  const unread = inv.unread_count || 0;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, '&:hover': { boxShadow: 2 }, transition: 'box-shadow 120ms' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {inv.organiser_name || 'Organiser'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {inv.event_name}
            </Typography>
            {inv.event_date && (
              <Typography variant="caption" color="text.secondary" display="block">
                {new Date(inv.event_date).toLocaleDateString()}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {unread > 0 && (
              <Chip label={`${unread} msg`} size="small" color="primary" variant="filled" sx={{ fontSize: 11 }} />
            )}
            <Chip
              icon={RSVP_ICON[inv.rsvp_status]}
              label={RSVP_LABEL[inv.rsvp_status] || inv.rsvp_status}
              size="small"
              color={RSVP_COLOR[inv.rsvp_status] || 'default'}
              variant="outlined"
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
          <Button size="small" variant="outlined" sx={{ textTransform: 'none', fontSize: 12 }}
            onClick={() => navigate(`/invite/view/${inv.invitation_id}`)}>
            View
          </Button>
          {inv.rsvp_status === 'pending' && (
            <>
              <Button size="small" variant="contained" color="success"
                sx={{ textTransform: 'none', fontSize: 12 }}
                onClick={() => onRsvp(inv.invitation_id, 'accepted')}>
                Accept
              </Button>
              <Button size="small" variant="outlined" color="error"
                sx={{ textTransform: 'none', fontSize: 12 }}
                onClick={() => onRsvp(inv.invitation_id, 'declined')}>
                Decline
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function InvitationsHub() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [campaigns, setCampaigns] = useState([]);
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sentData, receivedData] = await Promise.all([
        invitationApi.listSent(),
        invitationApi.listReceived(),
      ]);
      setCampaigns(sentData.campaigns || []);
      setReceived(receivedData.invitations || []);
    } catch {
      showNotification('Failed to load invitations', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const handleRsvp = async (invitationId, status) => {
    try {
      await invitationApi.rsvp(invitationId, status);
      showNotification(`RSVP ${status}`, 'success');
      load();
    } catch {
      showNotification('Failed to update RSVP', 'error');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await invitationApi.deleteCampaign(campaignId);
      showNotification('Invitation deleted', 'success');
      load();
    } catch {
      showNotification('Failed to delete invitation', 'error');
    }
  };

  const isEmpty = campaigns.length === 0 && received.length === 0;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: isMobile ? 8 : 0 }}>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={700}>Invitations</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/invitations/create')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Create Invitation
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : isEmpty ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <MailOutlineIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No invitations yet.</Typography>
            <Typography variant="body2" mt={0.5}>
              Create an invitation for one of your events to get started.
            </Typography>
            <Button variant="outlined" sx={{ mt: 2, textTransform: 'none' }}
              onClick={() => navigate('/invitations/create')}>
              Create your first invitation
            </Button>
          </Box>
        ) : (
          <Stack spacing={3}>
            {campaigns.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <SendIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    My Invitations ({campaigns.length})
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {campaigns.map(c => (
                    <SentEventCard key={c.campaign_id} campaign={c} onDelete={handleDeleteCampaign} />
                  ))}
                </Stack>
              </Box>
            )}

            {campaigns.length > 0 && received.length > 0 && <Divider />}

            {received.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <MailOutlineIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Received ({received.length})
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {received.map(inv => (
                    <ReceivedCard key={inv.invitation_id} inv={inv} onRsvp={handleRsvp} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
